import { Api } from '@/src/store'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { useLegacyCard } from '@/src/common/hooks/useCardInformation'
import { useLogError } from '@/src/common/hooks/useLogError'
import { useEffect, useMemo, type FunctionComponent } from 'react'
import { GET_CARD_APPLICATION_STATUS } from '@/src/experiences/card-application/gql/queries'
import { create } from 'zustand'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import { useNewestApplication } from '@/src/experiences/card-application/hooks/useNewestApplication'
import { useFeatureGate } from '@/src/services/Statsig/useFeatureGate'
import { FeatureGates } from '@/src/common/enums/FeatureGates'

const useApplicationStatusInitialized = create(() => false)

type Application =
  (typeof Api.endpoints)['GET /card/onboarding/applications']['Types']['ResultType']['applications'][number]

export interface CardGateProps {
  /** @deprecated are you sure you don't want to use {@link applicationStatusInitialized} instead? {@link isApplicationStatusLoading} can go back to the loading state multiple times within a session. it is also not guaranteed to be true on app init because the token needs to be available before the fetch is made */
  isApplicationStatusLoading: boolean
  applicationStatusInitialized: ReturnType<typeof useApplicationStatusInitialized.getState>
  refetchApplicationStatus: () => Promise<unknown>
  /** on subsequent fetches, {@link data} will always be non-null, even if the server returns nothing (in which case {@link data.getCreditCardInformation.node} will be null, not {@link data}) */
  firstCardFetchFinished?: object
  showCardManagement?: boolean | ''
  newestApplication?: Application
}

type ToWrapComponent = FunctionComponent<CardGateProps>

/** declaring this on the top-level to avoid unnecessary reference loss and consequent re-renders */
function placeholderRefetchWhileUnloaded() {
  return Promise.resolve()
}

export function withCardGate(ToWrap: ToWrapComponent) {
  return function Wrapped() {
    const applicationStatusInitialized = useApplicationStatusInitialized()
    const query = useNewestApplication()

    if (!query.data) {
      return (
        <ToWrap
          isApplicationStatusLoading={query.isFetching}
          applicationStatusInitialized={applicationStatusInitialized}
          refetchApplicationStatus={
            query.isUninitialized || query.isLoading
              ? placeholderRefetchWhileUnloaded
              : query.refetch
          }
          firstCardFetchFinished={undefined}
          showCardManagement={false}
        />
      )
    }

    if (query.data.eligibility.canStartCardApplication) {
      return (
        <PlatformCardGate
          ToWrap={ToWrap}
          platform={query.data.eligibility.platform}
          newestApplication={query.data.newestApplication}
        />
      )
    }

    switch (query.data.newestApplication?.platform) {
      case 'LOANPRO':
        return <NewCardGate ToWrap={ToWrap} newestApplication={query.data.newestApplication} />
      default:
        return (
          <LegacyCardGateForNewApplication
            ToWrap={ToWrap}
            newestApplication={
              query.data.newestApplication
                ? ({
                    ...query.data.newestApplication,
                    platform: query.data.newestApplication.platform,
                  } as const)
                : undefined
            }
          />
        )
    }
  }
}

type LegacyApplication = Omit<Application, 'platform'> & { platform: 'HIGHNOTE' }

function LegacyCardGateForNewApplication(props: {
  ToWrap: ToWrapComponent
  newestApplication?: LegacyApplication
}) {
  const logError = useLogError()
  /** @see {@link https://github.com/Acre-Rewards/acre-rewards-api/blob/329630a488601be712fed89b2f24340add979070/src/card/onboarding/onboarding.service.ts#L406-L408} */
  const mesaCardFeatureGate = useFeatureGate(FeatureGates.MesaCardEnabled)
  const mesaCardFeatureGateLoadedAndEnabled =
    mesaCardFeatureGate.loadingStatus === 'Ready' && mesaCardFeatureGate.featureGate.value
  useEffect(() => {
    if (!mesaCardFeatureGateLoadedAndEnabled) return
    if (!props.newestApplication)
      logError(
        `platform somehow undefined in LegacyCardGateForNewApplication`,
        props.newestApplication,
      )
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    else if (props.newestApplication.platform !== 'HIGHNOTE')
      logError(`LegacyCardGateForNewApplication isn't handling`, props.newestApplication.platform)
  }, [props.newestApplication, logError, mesaCardFeatureGateLoadedAndEnabled])

  return <LegacyCardGate ToWrap={props.ToWrap} newestApplication={props.newestApplication} />
}

function PlatformCardGate(props: {
  ToWrap: ToWrapComponent
  platform: Extract<
    (typeof Api.endpoints)['GET /card/onboarding/applications']['Types']['ResultType']['eligibility'],
    { platform: unknown }
  >['platform']
  newestApplication?: Application
}) {
  const logError = useLogError()
  return useMemo(() => {
    switch (props.platform) {
      case 'LOANPRO':
        return <NewCardGate ToWrap={props.ToWrap} newestApplication={props.newestApplication} />
      default:
        // if this check fails, there's at least one platform for which we haven't explicitly decided which card gate to render
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if ((props.platform satisfies 'HIGHNOTE') !== 'HIGHNOTE')
          logError(`PlatformCardGate isn't handling`, props.platform)
        return (
          <LegacyCardGate
            ToWrap={props.ToWrap}
            newestApplication={
              props.newestApplication
                ? ({ ...props.newestApplication, platform: props.platform } as const)
                : undefined
            }
          />
        )
    }
  }, [props.platform, props.ToWrap, logError, props.newestApplication])
}

function NewCardGate(props: { ToWrap: ToWrapComponent; newestApplication?: Application }) {
  const query = useCardAccount()
  useEffect(() => {
    if (query.isSuccess || query.error) useApplicationStatusInitialized.setState(true)
  }, [query.isSuccess, query.error])

  return (
    <props.ToWrap
      isApplicationStatusLoading={query.isFetching}
      applicationStatusInitialized={useApplicationStatusInitialized()}
      refetchApplicationStatus={
        query.isUninitialized || query.isLoading ? placeholderRefetchWhileUnloaded : query.refetch
      }
      firstCardFetchFinished={query.data}
      showCardManagement={
        props.newestApplication?.status === 'APPROVAL_ACKED' && Boolean(query.data?.length)
      }
      newestApplication={props.newestApplication}
    />
  )
}

function LegacyCardGate(props: { ToWrap: ToWrapComponent; newestApplication?: LegacyApplication }) {
  const { data } = useLegacyCard()
  const onError = useLogError()
  const {
    data: applicationStatusData,
    loading: isApplicationStatusLoading,
    refetch: refetchApplicationStatus,
  } = useLoggedInQuery(GET_CARD_APPLICATION_STATUS, {
    fetchPolicy: 'cache-and-network',
    onCompleted() {
      useApplicationStatusInitialized.setState(true)
    },
    onError(...args) {
      useApplicationStatusInitialized.setState(true)
      onError(...args)
    },
  })

  const showCardManagement =
    data?.getCreditCardInformation.node?.id &&
    applicationStatusData?.getCardProductApplicationStatus.status === 'ISSUED'

  return (
    <props.ToWrap
      isApplicationStatusLoading={isApplicationStatusLoading}
      applicationStatusInitialized={useApplicationStatusInitialized()}
      refetchApplicationStatus={refetchApplicationStatus}
      firstCardFetchFinished={data}
      showCardManagement={showCardManagement}
      newestApplication={props.newestApplication}
    />
  )
}
